import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  Button,
} from '@scio3/ui';
import { PLAN_FEATURES } from '@scio3/shared-types';
import { useBillingDialogStore, openUpgradeWizard } from '../stores/billing-dialog-store';
import { getPlanDisplayName } from '../utils/plan-helpers';

interface UpgradeRequiredAsset {
  id?: string | null;
  name?: string;
  appName?: string;
}

// Default monthly USD price per device for the user's current plan — matches
// scio1's currentDeviceProduct.plans.usd.monthly.price for the default product
// shown in engagePlanConfirmModal.
const PLAN_PRICE_USD_MONTHLY: Record<string, number> = {
  free: 0,
  standard: 10,
  pro: 13,
  proPlus: 13,
  engage: 30,
};

export function UpgradeRequiredDialog() {
  const { t } = useTranslation();
  const activeDialog = useBillingDialogStore((s) => s.activeDialog);
  const dialogContext = useBillingDialogStore((s) => s.dialogContext);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);

  const minPlanRequired = (dialogContext.minPlanRequired as string) ?? '';
  const currentPlan = (dialogContext.currentPlan as string) ?? '';
  const assets = (dialogContext.assets as UpgradeRequiredAsset[] | undefined) ?? [];
  const title = (dialogContext.title as string) ?? t('Upgrade Required');
  const customSubtitle = dialogContext.subtitle as string | undefined;
  // SCIO1 parity (#1181): when backend returns the affected assets, show the
  // "Your current plan - X - doesn't support these features" message with the
  // asset list — matches scio1 engagePlanConfirmModal. Falls back to the
  // generic plan-required line when no asset list is provided.
  const showAssetList = assets.length > 0 && currentPlan;
  const currentPlanName = currentPlan ? getPlanDisplayName(currentPlan) : '';
  const currentPlanPrice = PLAN_PRICE_USD_MONTHLY[currentPlan] ?? 0;
  const isEngageUpgrade = minPlanRequired === PLAN_FEATURES.ENGAGE;
  const fallbackSubtitle =
    customSubtitle ??
    t('This feature requires the {{plan}} plan or higher', {
      plan: getPlanDisplayName(minPlanRequired),
    });

  const handleUpgrade = useCallback(() => {
    openUpgradeWizard(minPlanRequired);
  }, [minPlanRequired]);

  const isOpen = activeDialog === 'upgradeRequired';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent
        className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-md"
        data-testid="upgrade-required-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-3">
          {showAssetList ? (
            <>
              {isEngageUpgrade ? (
                <p className="text-sm">
                  {t('Engage license is required for the following interactive asset(s):')}
                </p>
              ) : (
                <p className="text-sm">
                  {t('Your current plan')} -{' '}
                  <strong className="font-semibold">{currentPlanName}</strong> -{' '}
                  {t("doesn't support these features.")}
                </p>
              )}
              <ul className="text-sm space-y-1 pl-2">
                {assets.map((asset, i) => (
                  <li key={asset.id ?? i} className="font-semibold">
                    - {asset.name}
                    {asset.appName ? ` - ${asset.appName}` : ''}
                  </li>
                ))}
              </ul>
              {isEngageUpgrade && (
                <div className="space-y-2 pt-2">
                  <p className="text-sm font-semibold">{t('Important')}:</p>
                  <p className="text-sm">
                    {t('Interactive Engage Assets requires Engage license plan:')} $30/mo/device.
                  </p>
                  <p className="text-sm">
                    {t('You can review full')}{' '}
                    <a
                      href="https://www.optisigns.com/pricing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:text-primary/80"
                    >
                      {t('pricing plans comparison here.')}
                    </a>
                  </p>
                  <p className="text-sm">
                    {t('Your current plan is {{plan}} ${{price}}/mo/device', {
                      plan: currentPlanName,
                      price: currentPlanPrice,
                    })}
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{fallbackSubtitle}</p>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" className="cursor-pointer">
              {t('Cancel')}
            </Button>
          </DialogClose>
          <Button onClick={handleUpgrade} className="cursor-pointer">
            {showAssetList ? t('Update Subscription') : t('Upgrade')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
